import { complianceClass, type ComplianceStop } from 'src/features/reporting/utils/color';
import type { Action, ActionDisplayStatus } from './types';

/**
 * True when `dueDate` is a local calendar day before today.
 * Date-only strings are compared as Y-M-D in the user's timezone; parsing them
 * with `new Date(dueDate)` would treat UTC midnight as already past for most of the local day.
 * Missing or unparseable values are not before today.
 */
function isDueBeforeLocalToday(dueDate: string): boolean {
  const [year, month, day] = dueDate.split('T')[0].split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  const due = new Date(year, month - 1, day);
  if (due.getFullYear() !== year || due.getMonth() !== month - 1 || due.getDate() !== day) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return due < today;
}

export function getActionDisplayStatus(action: Pick<Action, 'status' | 'due_date'>): ActionDisplayStatus {
  if (action.status === 'open' && action.due_date && isDueBeforeLocalToday(action.due_date)) {
    return 'overdue';
  }
  return action.status;
}

export function actionStatusLabel(status: ActionDisplayStatus): string {
  switch (status) {
    case 'overdue':
      return 'Overdue';
    case 'resolved':
      return 'Resolved';
    case 'archived':
      return 'Archived';
    case 'open':
    default:
      return 'Open';
  }
}

export function actionStatusRating(status: ActionDisplayStatus): ComplianceStop {
  switch (status) {
    case 'overdue':
      return 1;
    case 'open':
      return 4;
    case 'resolved':
      return 5;
    case 'archived':
    default:
      return 'na';
  }
}

export function actionStatusPillClass(status: ActionDisplayStatus): string {
  return complianceClass(actionStatusRating(status));
}
